/**
 * @file GameRoom.tsx
 * @description
 * The main game room page component.
 * Fetches game state using the useGameState hook and renders the GameBoard or Waiting Room view.
 * Displays loading, error, and game end states.
 * Includes a player info banner and logout button.
 * Integrates the ChatBox component for communication.
 * Implements Host Controls UI (Start Game, Reassign Host, Kick Player, End Game, Set Current Player, Set Required Plays).
 * Adds buttons for players to concede or for anyone to leave the game.
 *
 * **UPDATE:** Added an "Undo Last Move" button visible during the current player's turn,
 * enabled only after a move has been made.
 */

import React, { useEffect, useState } from 'react'; // Import useState
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import GameBoard from '../components/GameBoard';
import ChatBox from '../components/ChatBox';
import useGameState from '../hooks/useGameState';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import PlayerInfoBanner from '../components/PlayerInfoBanner';

const GameRoom: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const { socket } = useSocket();
  const { isAuthenticated, playerName } = useAuth();

  const {
      state,
      isLoading,
      error,
      isSpectator,
      chatMessages,
      chatError,
      playCard,
      undoMove, // Get the undoMove action from the hook
      endTurn,
      hostAction, // From useGameState hook
      sendChatMessage,
      concedeGame, // New concede action
      leaveGame, // New leave game action
      validateMove
    } = useGameState();

  const currentPlayerId = socket?.id || null;

  const navigate = useNavigate();

  // Determine if the current client user is a player in the current state
  const isPlayer = state?.players?.some(p => p.id === currentPlayerId) || false;
  // Determine if the current client user can send chat messages (only players)
  const canSendChatMessages = isPlayer;
  // Determine if the current client user is the host
  const isHost = currentPlayerId === state?.hostId; // Use optional chaining for state
  // Determine if it's the current client user's turn
  const isMyTurn = state?.currentPlayerIndex !== undefined && state?.players[state.currentPlayerIndex]?.id === currentPlayerId;

   // Determine if the undo button should be enabled
   // Enabled if it's my turn, I'm a player, and there is a last move recorded in state.
   // The backend handler will verify the move was by THIS player. Frontend only needs state check.
   const canUndoMove = isMyTurn && isPlayer && state?.lastMove !== undefined && state.lastMove !== null;


   // State for managing the visibility of host management controls
   const [showManagePlayers, setShowManagePlayers] = useState(false);

   // State for the input field for setting required plays
   const [requiredPlaysInput, setRequiredPlaysInput] = useState<string>('');


   // State for tracking if a leave game action is in progress
   const [isLeaving, setIsLeaving] = useState(false);

   // Handle leave game action
   const handleLeaveGame = async () => {
       if (!code || isLeaving || !socket?.connected) return; // Prevent if busy, socket down, or missing code
       if (!window.confirm('Are you sure you want to leave this game?')) {
            return; // Ask for confirmation
       }

       setIsLeaving(true);
       console.log(`Attempting to leave game ${code}...`);
       try {
            await leaveGame(); // Call the leave game action
            console.log(`Successfully left game ${code}. Navigating to lobby.`);
            // Navigate to the lobby page after successful leave
            navigate('/lobby', { replace: true });
       } catch (leaveError) {
           console.error('Failed to leave game:', leaveError);
           // Error is already alerted by the hook, just stop loading state
       } finally {
           setIsLeaving(false);
       }
   };

   // Handle concede game action (available to the current player)
   const handleConcedeGame = () => {
        if (!state || !isPlayer || !isMyTurn || !socket?.connected) {
             console.warn('Cannot concede: Not player, not my turn, or socket not connected.');
             return;
        }
        if (!window.confirm('Are you sure you want to concede the game? This will end the game as a loss.')) {
            return; // Ask for confirmation
       }
       console.log(`Attempting to concede game ${code}...`);
       concedeGame(); // Call the concede action (hook handles feedback)
       // The hook's gameEnded listener will trigger the end game UI/navigation.
       // No local state changes or navigation needed here.
   };

    // Handle setting required plays (Host Only)
    const handleSetRequiredPlays = () => {
         if (!state || !isHost || !socket?.connected) {
             console.warn('Cannot set required plays: Not host or socket not connected.');
             alert('Only the host can set required plays.');
             return;
         }

         const parsedPlays = parseInt(requiredPlaysInput, 10);
         if (isNaN(parsedPlays) || parsedPlays < 1) {
             alert('Please enter a valid number for required plays (>= 1).');
             setRequiredPlaysInput(''); // Clear invalid input
             return;
         }

         console.log(`Attempting to set required plays to ${parsedPlays} for game ${code}...`);
         hostAction('setRequiredPlays', { requiredPlays: parsedPlays }); // Call the host action
         setRequiredPlaysInput(''); // Clear input after sending
    };

   // Handle Undo Move action
   const handleUndoMove = () => {
       if (!canUndoMove || !socket?.connected) {
            console.warn('Cannot undo move: Condition not met.');
            // UI button should be disabled, so this shouldn't be reachable via click.
            return;
       }
        console.log(`Attempting to undo move for game ${code}...`);
        undoMove(); // Call the undoMove action
        // The hook's stateUpdate listener will handle state changes.
   };


  // Handle cases where code is missing from URL (shouldn't happen with proper routing configuration)
  // This check should happen early, before calling the hook that relies on `code`.
  // However, the hook *itself* uses useParams, so it will handle the missing code internally.
  // Let the hook handle initial loading/error based on missing code.

  // If not authenticated (AuthRedirect handles this, but defensive check)
   if (isAuthenticated === false) {
       console.warn('User not authenticated in GameRoom, redirecting to login.');
       return <Navigate to="/login" replace />;
   }
   // If authentication status is still unknown, show nothing or loading (AuthRedirect handles this primarily)
    if (isAuthenticated === null) {
        return null; // AuthRedirect handles showing loading
    }


  // --- Render based on game state and loading status ---

  if (isLoading) {
    return (
        <div className="flex flex-col items-center justify-center h-screen text-gray-900 dark:text-gray-100">
            <PlayerInfoBanner />
            Loading Game {code}...
        </div>
    );
  }

  if (error) {
    console.error('Game room error:', error);
    return (
      <div className="flex flex-col items-center justify-center h-screen text-red-600 dark:text-red-400">
         <PlayerInfoBanner />
        <h1 className="text-2xl font-bold mb-4">Error</h1>
        <p>{error.message}</p>
        <button onClick={() => navigate('/lobby')} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Back to Lobby
        </button>
      </div>
    );
  }

    if (!state) {
        console.error('Game state is null after loading, this should not happen.');
         // Navigate away if state is unexpectedly null after loading
         return <Navigate to="/lobby" replace />;
    }


  // If game is ended
   if (state.status === 'ended') {
       const result = state.gameEndedResult;
       const message = state.gameEndedMessage || (result === 'win' ? 'All cards played!' : 'Game ended.');
       // Show names of players who were in the game state when it ended
       const playerNames = state.players.map(p => p.name).join(', ');

       return (
            <div className="game-ended-container"> {/* Use CSS class */}
                 <PlayerInfoBanner />
                <h1 className="text-4xl font-bold mb-4">Game Ended</h1> {/* Keep Tailwind class for simplicity */}
                 <p className="result-message">{result === 'win' ? 'Victory!' : 'Defeat!'}</p> {/* Use CSS class */}
                <p className="end-message">{message}</p> {/* Use CSS class */}
                 {state.players.length > 0 && <p className="player-list">Players: {playerNames}</p>} {/* Use CSS class */}
                 <button onClick={() => navigate('/lobby')} className="button button-primary" disabled={isLeaving || !socket?.connected}> {/* Use CSS classes */}
                     {isLeaving ? 'Leaving...' : 'Back to Lobby'}
                 </button>
            </div>
       );
   }


    // If game is waiting (Waiting Room view)
    if (state.status === 'waiting') {
        return (
            <div className="waiting-room-container"> {/* Use CSS class */}
                 <PlayerInfoBanner />
                <h1>Waiting Room - Game {code}</h1>
                 <p className="game-id">Game ID: {state.gameId}</p> {/* Use CSS class */}
                 <p className="status-message">Waiting to Start</p> {/* Use CSS class */}
                <h2>Players ({state.players.length}/5)</h2>
                <ul>
                    {state.players.map(player => (
                         <li key={player.id}>
                            {player.name} {player.id === state.hostId && '(Host)'} {player.id === currentPlayerId && '(You)'}
                         </li>
                    ))}
                </ul>
                {state.spectators.length > 0 && (
                     <>
                        <h2>Spectators ({state.spectators.length})</h2>
                         <ul>
                             {state.spectators.map(spectatorId => (
                                  <li key={`spec-${spectatorId}`}>Spectator {spectatorId === currentPlayerId ? '(You)' : spectatorId.substring(0, 6)}</li>
                             ))}
                         </ul>
                     </>
                )}


                 {/* Start Game Button (Host Only) */}
                 {isPlayer && isHost && state.players.length > 0 && (
                     <div className="waiting-room-controls"> {/* Use CSS class */}
                         <button onClick={() => hostAction('startGame', {})} disabled={state.players.length === 0 || !socket?.connected} className="button button-primary"> {/* Use CSS classes */}
                             Start Game ({state.players.length} player{state.players.length !== 1 ? 's' : ''})
                         </button>
                          {state.players.length === 0 && <p>Waiting for players...</p>}
                     </div>
                 )}
                  {/* Message if not host */}
                   {isPlayer && !isHost && (
                       <p style={{ marginTop: '1rem' }}>Waiting for the host ({state.players.find(p => p.id === state.hostId)?.name || 'Unknown'}) to start the game.</p>
                   )}

                {/* Host Management Button (Host Only, Waiting or Started) */}
                {isHost && state.players.length > 0 && ( // Only show if host and there are players/spectators to manage
                     <div style={{ marginTop: '1rem', position: 'relative' }}> {/* Position relative for dropdown */}
                         <button onClick={() => setShowManagePlayers(!showManagePlayers)} className="button"> {/* Use CSS class */}
                             {showManagePlayers ? 'Hide Management' : 'Manage Players'}
                         </button>
                         {showManagePlayers && (
                            <div className="host-management-container"> {/* Use CSS class */}
                                 <h3>Players</h3>
                                 <ul>
                                     {/* List all players, including host, for management actions */}
                                     {state.players.map(player => (
                                         <li key={player.id}>
                                             <span>{player.name} {player.id === currentPlayerId && '(You)'}</span>
                                             <div className="host-management-actions"> {/* Use CSS class */}
                                                 {/* Reassign Host (Cannot reassign to yourself) */}
                                                 {player.id !== currentPlayerId && (
                                                     <button onClick={() => hostAction('reassign', { newHostId: player.id })} disabled={!socket?.connected} className="button">Make Host</button>
                                                 )}
                                                  {/* Set Current Player (Can set anyone, including self) */}
                                                  {/* Only show this in started games? Or allow setting turn for next game? Let's assume only in started. */}
                                                  {/* <button onClick={() => hostAction('setCurrentPlayer', { playerId: player.id })} className="button" disabled={!socket?.connected}>Set as Current Player</button> */} {/* Use CSS class */}
                                             </div>
                                         </li>
                                     ))}
                                 </ul>
                                  {/* Manage Spectators */}
                                  {state.spectators.length > 0 && (
                                      <>
                                         <h4>Spectators</h4>
                                          <ul>
                                              {state.spectators.map(spectatorId => (
                                                  <li key={`spec-${spectatorId}`}>
                                                      <span>Spectator {spectatorId === currentPlayerId ? '(You)' : spectatorId.substring(0, 6)}</span>
                                                  </li>
                                              ))}
                                          </ul>
                                      </>
                                  )}
                                   {/* Set Required Plays (Host Only, Started Game - not visible in Waiting) */}
                             </div>
                         )}
                     </div>
                )}


                {/* End Game Button (Host Only, Waiting or Started) */}
                 {isHost && ( // Only show if host
                      <div style={{ marginTop: '1rem' }}> {/* Position remains relative to flow in waiting room */}
                         <button onClick={() => hostAction('end', {})} disabled={!socket?.connected} className="button button-danger"> {/* Use CSS classes */}
                              End Game (Loss) (Host)
                          </button>
                       </div>
                  )}

                 {/* Leave Game Button (Any player/spectator) */}
                  <div style={{ marginTop: '2rem' }}> {/* Position remains relative to flow in waiting room */}
                      <button onClick={handleLeaveGame} disabled={isLeaving || !socket?.connected} className="button"> {/* Use CSS class */}
                          {isLeaving ? 'Leaving...' : 'Leave Game'}
                      </button>
                  </div>


                {/* Chat Box in Waiting Room */}
                 <div className="waiting-room-chat"> {/* Use CSS class */}
                     <ChatBox
                         messages={chatMessages}
                         onSendMessage={sendChatMessage}
                         errorMessage={chatError}
                          canSendMessages={canSendChatMessages}
                     />
                 </div>

            </div>
        );
    }


  // If game is started, render the GameBoard
  if (state.status === 'started') {
    return (
        <div style={{ padding: '1rem', position: 'relative', minHeight: 'calc(100vh - 40px)' /* Adjust for banner height */ }}> {/* Relative positioning for children */}
            <PlayerInfoBanner />
             {/* GameBoard handles its own relative positioning and player layout */}
              <GameBoard
                  state={state}
                  currentPlayerId={currentPlayerId}
                   isSpectator={isSpectator} // Pass isSpectator to GameBoard
                  onPlayCard={playCard}
                  onEndTurn={endTurn}
                  validateMove={validateMove}
              />
             {/* Turn Control Buttons (End Turn, Concede, Undo) - Positioned at bottom center */}
             <div className="button-group">
                <div className="button-row">
                <div className="game-controls-bottom-center"> {/* Use CSS class */}
                    {!isSpectator && ( // Only show if it's my turn AND I'm a player
                        <>
                            {/* End Turn Button */}
                            <button
                            onClick={endTurn} // Use the handler from props
                            disabled={!isMyTurn ||state.requiredPlays > 0 || !socket?.connected} // Disable if not enough cards played or socket disconnected
                            className="button end-turn-button" // Use CSS classes
                            >
                            End Turn
                            </button>
                        </>
                    )}
                </div>
                </div>
                {/* Turn Control Buttons (End Turn, Concede, Undo) - Positioned at bottom center */}
                <div className="button-row">
                <div className="game-controls-bottom-center"> {/* Use CSS class */}
                    {!isSpectator && ( // Only show if it's my turn AND I'm a player
                        <>
                            {/* Undo Last Move Button */}
                            <button
                                onClick={handleUndoMove}
                                disabled={!isMyTurn || !canUndoMove || !socket?.connected} // Disable if cannot undo or socket disconnected
                                className="button" // Use CSS class
                            >
                                Undo Last Move
                            </button>

                            {/* Concede Button */}
                            <button
                                onClick={handleConcedeGame}
                                disabled={!isMyTurn || !socket?.connected} // Disable if socket disconnected
                                className="button concede-button" // Use CSS classes
                            >
                                Concede (Loss)
                            </button>
                        </>
                    )}

                </div>

                </div>
             </div>

             <div>
                <button onClick={handleLeaveGame} disabled={isLeaving || !socket?.connected} className="button"> {/* Use CSS class */}
                        {isLeaving ? 'Leaving...' : 'Leave Game :('}
                </button>
             </div>


            {/* Host Management Button (Host Only, Started) */}
            {isHost && state.players.length > 0 && ( // Only show if host and there are players to manage
                 <div className="game-controls-top-right">
                     <button onClick={() => setShowManagePlayers(!showManagePlayers)} className="button"> {/* Use CSS class */}
                         {showManagePlayers ? 'Hide Management' : 'Manage Game'}
                     </button>
                     {showManagePlayers && (
                        <div className="host-management-container"> {/* Use CSS class */}
                             <h3>Players</h3>
                             <ul>
                                 {/* List all players, including host, for management actions */}
                                 {state.players.map(player => (
                                     <li key={player.id}>
                                         <span>{player.name} {player.id === currentPlayerId && '(You)'}</span>
                                         <div className="host-management-actions"> {/* Use CSS class */}
                                             {player.id !== currentPlayerId && (
                                                 <button onClick={() => hostAction('reassign', { newHostId: player.id })} disabled={!socket?.connected} className="button">Make Host</button>
                                             )}
                                             <button onClick={() => hostAction('setCurrentPlayer', { playerId: player.id })} disabled={!socket?.connected} className="button">Set as Current Player</button>
                                         </div>
                                     </li>
                                 ))}
                             </ul>
                              {/* Manage Spectators */}
                               {state.spectators.length > 0 && (
                                   <>
                                      <h4>Spectators</h4>
                                       <ul>
                                           {state.spectators.map(spectatorId => (
                                               <li key={`spec-${spectatorId}`}>
                                                   <span>Spectator {spectatorId === currentPlayerId ? '(You)' : spectatorId.substring(0, 6)}</span>
                                               </li>
                                           ))}
                                       </ul>
                                   </>
                               )}
                             {/* Set Required Plays (Host Only, Started Game) */}
                             <div className="set-required-plays" style={{ alignItems: 'center' }}> {/* Use CSS class */}
                                  <h4>Override Required Plays</h4>
                                  <div style={{ display: 'flex', alignItems: 'center' }}> {/* Keep flex for inner layout */}
                                      <input
                                           type="number"
                                           min="1" // Minimum required plays is 1
                                           placeholder="e.g., 2"
                                           value={requiredPlaysInput}
                                           onChange={(e) => setRequiredPlaysInput(e.target.value)}
                                           className="input-text set-required-plays-input" // Use CSS classes
                                           disabled={!socket?.connected}
                                      />
                                      <button onClick={handleSetRequiredPlays} disabled={!requiredPlaysInput || !socket?.connected} className="button"> {/* Use CSS class */}
                                           Set
                                      </button>
                                  </div>
                                   <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>Current: {state.requiredPlays}</p>
                             </div>
                        </div>
                     )}
                 </div>
            )}
            {/* Chat Box in Started Game */}
             <div className="game-chat-bottom-right"> {/* Use CSS class */}
                 <ChatBox
                     messages={chatMessages}
                     onSendMessage={sendChatMessage}
                     errorMessage={chatError}
                      canSendMessages={canSendChatMessages}
                 />
             </div>

            {/* TODO: Add player/spectator list toggle/view */}
        </div>
    );
  }

   console.warn('GameRoom: Reached unexpected state render path.', { state, isLoading, error });
   return <div className="flex items-center justify-center h-screen text-gray-900 dark:text-gray-100">Loading Game...</div>;
};

export default GameRoom;
